import { Component, effect, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@devinforius/super-compos-17';
import { TranslateService as IEssentialsTranslateService } from '@devinforius/i-essentials';
import { VERSIONS_RELEASE_NOTES } from './core/consts';
import { Versioning } from './core/models/versioning';
import { SsoService } from '@devinforius/i-sso';
import { Router } from '@angular/router';
import { SpellcheckerService } from '@devinforius/i-dx-editor';
import { ToasterService } from '@devinforius/i-essentials';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogReleasesNotesComponent } from '@shared/components/modals/dialog-releases-notes/dialog-releases-notes.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [DatePipe],
})
export class AppComponent implements OnInit, OnDestroy {
  //Here to initialise options globally
  private readonly ssoService = inject(SsoService);
  private readonly spellcheckerService = inject(SpellcheckerService);
  public readonly versioning = inject(Versioning);
  private readonly router = inject(Router);
  private readonly toaster = inject(ToasterService);
  private readonly iEssentialsTranslateService = inject(IEssentialsTranslateService);
  public readonly releaseNotes = VERSIONS_RELEASE_NOTES;

  title = 'idelibebackoffice';
  loading: boolean = false;

  private redirected = false;

  public isConnectedEff = effect(() => {
    if (this.redirected) return;

    if (this.ssoService.isConnected() || this.ssoService.hasValidAccessToken()) {
      if (this.router.url.startsWith('/callback')) {
        this.redirected = true;

        const redirect = localStorage.getItem('post_login_redirect_uri') || '/private';

        localStorage.removeItem('post_login_redirect_uri');
        this.router.navigateByUrl(redirect);
      }
    }
  });

  constructor(
    private translateService: TranslateService,
    private primeNGConfig: PrimeNGConfig,
    private el: ElementRef,
    private renderer: Renderer2,
    private dialogService: DialogService
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.toaster.setKey('idelibe-toast');

    if (localStorage.getItem('lang') === null) {
      const userLang = navigator.language.split('-').reverse()[0].toLowerCase();
      if (userLang === 'fr' || userLang === 'en' || userLang === 'nl') {
        localStorage.setItem('lang', userLang);
      } else {
        localStorage.setItem('lang', 'fr');
      }
    }

    const lang = localStorage.getItem('lang');
    const langs = ['fr', 'en', 'nl'];
    const translationFolders = ['assets/translations'];

    await this.translateService.init(lang, langs, translationFolders);
    await this.iEssentialsTranslateService.init(lang, langs, translationFolders);

    this.initialisePrimeNGTranslations();

    this.spellcheckerService.setWorker(
      new Worker(new URL('./spellchecker/spellchecker.worker', import.meta.url), {
        type: 'module',
      })
    );

    const loader = this.el.nativeElement.parentElement.querySelector('.loaderPage');
    if (loader) {
      this.renderer.setStyle(loader, 'display', 'none');
    }

    this.translateService.onTranslationReady.subscribe((isReady: boolean) => {
      if (isReady) {
        this.loading = false;
      } else {
        this.loading = true;
      }
    });

    this.openReleaseNotesDialogIfNeeded();
  }

  private openReleaseNotesDialogIfNeeded(): void {
    if (this.versioning.upToDate()) return;

    this.dialogService.open(DialogReleasesNotesComponent, {}).onClose.subscribe(() => {
      this.versioning.update();
    });
  }

  public ngOnDestroy(): void {
    this.isConnectedEff.destroy();
  }

  private initialisePrimeNGTranslations(): void {
    this.primeNGConfig.setTranslation({
      startsWith: this.translateService.translate('PRIMENG.STARTSWITH'),
      contains: this.translateService.translate('PRIMENG.CONTAINS'),
      notContains: this.translateService.translate('PRIMENG.NOTCONTAINS'),
      endsWith: this.translateService.translate('PRIMENG.ENDSWITH'),
      equals: this.translateService.translate('PRIMENG.EQUALS'),
      notEquals: this.translateService.translate('PRIMENG.NOTEQUALS'),
      noFilter: this.translateService.translate('PRIMENG.NOFILTER'),
      lt: this.translateService.translate('PRIMENG.LT'),
      lte: this.translateService.translate('PRIMENG.LTE'),
      gt: this.translateService.translate('PRIMENG.GT'),
      gte: this.translateService.translate('PRIMENG.GTE'),
      is: this.translateService.translate('PRIMENG.IS'),
      isNot: this.translateService.translate('PRIMENG.ISNOT'),
      before: this.translateService.translate('PRIMENG.BEFORE'),
      after: this.translateService.translate('PRIMENG.AFTER'),
      dateIs: this.translateService.translate('PRIMENG.DATEIS'),
      dateIsNot: this.translateService.translate('PRIMENG.DATEISNOT'),
      dateBefore: this.translateService.translate('PRIMENG.DATEBEFORE'),
      dateAfter: this.translateService.translate('PRIMENG.DATEAFTER'),
      clear: this.translateService.translate('PRIMENG.CLEAR'),
      apply: this.translateService.translate('PRIMENG.APPLY'),
      matchAll: this.translateService.translate('PRIMENG.MATCHALL'),
      matchAny: this.translateService.translate('PRIMENG.MATCHANY'),
      addRule: this.translateService.translate('PRIMENG.ADDRULE'),
      removeRule: this.translateService.translate('PRIMENG.REMOVERULE'),
      accept: this.translateService.translate('PRIMENG.ACCEPT'),
      reject: this.translateService.translate('PRIMENG.REJECT'),
      choose: this.translateService.translate('PRIMENG.CHOOSE'),
      upload: this.translateService.translate('PRIMENG.UPLOAD'),
      cancel: this.translateService.translate('PRIMENG.CANCEL'),
      dayNames: [
        this.translateService.translate('PRIMENG.DAYNAMES.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.SATURDAY'),
      ],
      dayNamesShort: [
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.SATURDAY'),
      ],
      dayNamesMin: [
        this.translateService.translate('PRIMENG.DAYNAMESMIN.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.SATURDAY'),
      ],
      monthNames: [
        this.translateService.translate('PRIMENG.MONTHNAMES.JANUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.FEBRUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.MARCH'),
        this.translateService.translate('PRIMENG.MONTHNAMES.APRIL'),
        this.translateService.translate('PRIMENG.MONTHNAMES.MAY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.JUNE'),
        this.translateService.translate('PRIMENG.MONTHNAMES.JULY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.AUGUST'),
        this.translateService.translate('PRIMENG.MONTHNAMES.SEPTEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.OCTOBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.NOVEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.DECEMBER'),
      ],
      monthNamesShort: [
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JANUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.FEBRUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.MARCH'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.APRIL'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.MAY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JUNE'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JULY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.AUGUST'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.SEPTEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.OCTOBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.NOVEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.DECEMBER'),
      ],
      dateFormat: this.translateService.translate('PRIMENG.DATEFORMAT'),
      today: this.translateService.translate('PRIMENG.TODAY'),
      weekHeader: this.translateService.translate('PRIMENG.WEEKHEADER'),
      weak: this.translateService.translate('PRIMENG.WEAK'),
      medium: this.translateService.translate('PRIMENG.MEDIUM'),
      strong: this.translateService.translate('PRIMENG.STRONG'),
      passwordPrompt: this.translateService.translate('PRIMENG.PASSWORDPROMPT'),
      emptyMessage: this.translateService.translate('PRIMENG.EMPTYMESSAGE'),
      emptyFilterMessage: this.translateService.translate('PRIMENG.EMPTYFILTERMESSAGE'),
    });
  }
}
